import type { ChangeEvent, ClipboardEvent } from 'react';

type PostalCodes = {
  [K in SupportedCountry]: RegExp;
};

const postalCodeHyphenPosition = {
  US: 6,
  JP: 4,
};
const postalCodeMaxLength = {
  US: 10,
  JP: 7,
};

// Postal code regex
export const postalCodesRegex: PostalCodes = {
  // US ZIP code (U.S. postal code) allow both the five-digit and nine-digit (called ZIP + 4) formats.
  // E.g. a valid postal code should match 12345 and 12345-6789, but not 1234, 123456, 123456789,
  // or 1234-56789.
  US: /(^\d{5}$)|(^\d{5}-\d{4}$)/,
  JP: /(^\d{3}-\d{4}$)/,
};

type ValidatePostalCode = (countryCode: SupportedCountry, postalCode: string) => boolean;

/**
 * Checks if postal code is valid by provided country postal code
 */
export const validatePostalCode: ValidatePostalCode = (countryCode, postalCode) => {
  const regex = postalCodesRegex[countryCode];

  if (!regex) {
    return false;
  }

  return regex.test(postalCode);
};

/**
 * Postal code on change handler.
 * Postal code input must be a text input because Postal code can either be on five-digit and nine-digit (called ZIP + 4) formats.
 * Therefore we manipulate and parse the user input.
 * This handler logic only allows numbers characters and adds automatically "-" in between numbers if user wants nine-digit format.
 */
export const postalCodeOnChangeHandler = (
  e: ChangeEvent<HTMLInputElement>,
  country: SupportedCountry,
): void => {
  const { value } = e.target;
  const numberRgx = /^\d+$/;
  const lastCharacter = value.charAt(value.length - 1);
  const hyphenPosition = postalCodeHyphenPosition[country];
  const maxLength = postalCodeMaxLength[country];
  const isInBetweenChar = lastCharacter === '-' && value.length === hyphenPosition;

  if (!lastCharacter.match(numberRgx) && !isInBetweenChar) {
    e.target.value = value.slice(0, -1);
  } else if (value.length === hyphenPosition && lastCharacter.match(numberRgx)) {
    e.target.value = `${value.slice(0, -1)}-${lastCharacter}`;
  } else if (value.length > maxLength && !validatePostalCode(country, value)) {
    e.target.value = value.slice(0, -1);
  }
};

/**
 * This paste handler prevents user to only paste a valid postal code format
 */
export const postalCodeOnPasteHandler = (e: ClipboardEvent, country: SupportedCountry): void => {
  const clipboardData = e.clipboardData.getData('Text');

  if (!validatePostalCode(country, clipboardData)) {
    e.preventDefault();
  }
};
